import { Link, useNavigate } from "react-router-dom";
import { Heart, ShoppingBag, Star } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useStore } from "@/context/StoreContext";
import { useWishlist } from "@/context/WishlistContext";
import { inr } from "@/lib/format";
import BrandImage from "@/components/store/BrandImage";
import { srcsetFor } from "@/lib/srcset";

function bestCoupon(product, offers) {
  const price = product.price_min ?? 0;
  if (!offers?.length || !price) return null;
  let best = null;
  for (const o of offers) {
    if (o.min_order && price < o.min_order) continue;
    const disc = o.type === "percentage" ? price * o.value / 100 : Math.min(o.value, price);
    if (disc > 0 && (!best || disc > best.disc)) best = { code: o.code, disc, final: price - disc };
  }
  return best;
}

export default function ProductCard({ product, index = 0 }) {
  const { toggle, isWishlisted } = useWishlist();
  const { addItem } = useCart();
  const { offers } = useStore();
  const navigate = useNavigate();

  const isCoin = product.product_type === "coin";
  const wishlisted = isWishlisted(product.id);
  const secondImg = product.model_image || product.images?.[1];
  const isNew = product.created_at && (Date.now() - new Date(product.created_at).getTime()) < 30 * 86400000;
  const bestseller = (product.tags || []).includes("bestseller");
  const stockTotal = (product.variants || []).reduce((s, v) => s + (v.stock || 0), 0);
  const lowStock = stockTotal > 0 && stockTotal <= 3;
  const deal = bestCoupon(product, offers);
  const hasRating = (product.rating_count || 0) > 0;

  const quickAdd = (e) => {
    e.preventDefault();
    if (isCoin || product.personalisation?.enabled || (product.options || []).length > 1) {
      navigate(`/products/${product.slug}`);
      return;
    }
    const v = (product.variants || []).find((x) => x.stock > 0);
    if (!v) { toast.error("Out of stock"); return; }
    addItem(product, v, 1, null);
    toast.success("Added to cart");
  };

  return (
    <div className="group bg-white rounded-xl border border-brand/10 overflow-hidden transition-shadow hover:shadow-[0_8px_30px_rgba(55,34,72,0.10)] animate-fade-up"
      style={{ animationDelay: `${Math.min(index, 8) * 60}ms` }} data-testid={`product-card-${product.slug}`}>
      <div className="relative">
        <Link to={`/products/${product.slug}`} className="block aspect-square overflow-hidden bg-ivory">
          <BrandImage src={(product.images || [])[0]} alt={product.title}
            srcSet={srcsetFor((product.image_sizes || [])[0])}
            sizes="(max-width: 640px) 50vw, 25vw"
            className={`h-full w-full ${secondImg ? "group-hover:opacity-0 transition-opacity duration-300" : ""}`}
            imgClassName={secondImg ? "" : "group-hover:scale-[1.03] transition-transform duration-500"} />
          {secondImg && (
            <img src={secondImg} alt={`${product.title} on model`} loading="lazy"
              className="absolute inset-0 h-full w-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          )}
        </Link>

        {/* Ribbon badges — data-backed only */}
        <div className="absolute top-2 left-2 flex flex-col gap-1">
          {isCoin && <span className="bg-brand text-gold text-[9px] font-mono tracking-widest px-2 py-0.5 rounded-full" data-testid={`badge-999-${product.slug}`}>999 FINE</span>}
          {product.personalisation?.enabled && <span className="bg-gold text-brand text-[9px] font-mono tracking-widest px-2 py-0.5 rounded-full" data-testid={`badge-pers-${product.slug}`}>PERSONALISED</span>}
          {isNew && <span className="bg-white/90 text-brand text-[9px] font-mono tracking-widest px-2 py-0.5 rounded-full border border-brand/20">NEW</span>}
          {bestseller && <span className="bg-[#2E7D5B] text-white text-[9px] font-mono tracking-widest px-2 py-0.5 rounded-full">BESTSELLER</span>}
          {lowStock && <span className="bg-[#B3261E] text-white text-[9px] font-mono tracking-widest px-2 py-0.5 rounded-full" data-testid={`badge-lowstock-${product.slug}`}>ONLY {stockTotal} LEFT</span>}
        </div>

        <button
          onClick={(e) => { e.preventDefault(); toggle(product); }}
          className={`absolute top-2 right-2 h-8 w-8 rounded-full bg-white/90 border flex items-center justify-center transition-colors btn-press ${wishlisted ? "border-gold text-gold" : "border-brand/15 text-ink-2 hover:text-brand"}`}
          aria-label="Wishlist" data-testid={`wishlist-btn-${product.slug}`}>
          <Heart size={15} fill={wishlisted ? "currentColor" : "none"} />
        </button>

        <button onClick={quickAdd}
          className="absolute bottom-2 right-2 h-8 w-8 rounded-full bg-brand text-ivory flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity btn-press hover:bg-brand-2"
          aria-label="Add to cart" data-testid={`quick-add-${product.slug}`}>
          <ShoppingBag size={14} />
        </button>
      </div>

      <div className="p-3.5">
        <Link to={`/products/${product.slug}`}>
          <h3 className="font-heading font-semibold text-[15px] leading-snug line-clamp-1 hover:text-brand transition-colors">{product.title}</h3>
        </Link>
        <p className="text-[11px] font-mono text-ink-2 mt-0.5 uppercase tracking-wide">
          {(() => {
            const purs = [...new Set((product.variants || []).map((v) => v.purity || product.purity).filter(Boolean))];
            return purs.join("/") || product.purity;
          })()} {product.metal}{product.variants?.[0]?.net_weight ? ` · ${product.variants[0].net_weight}g` : ""}{product.carats ? ` · ${product.carats} ct` : ""}
        </p>
        {product.colours?.length > 0 && (
          <div className="flex gap-1.5 mt-1.5" data-testid={`swatches-${product.slug}`}>
            {product.colours.map((c) => (
              <span key={c} title={`${c} gold`}
                className="h-3 w-3 rounded-full border border-black/10"
                style={{ background: c === "Yellow" ? "#E4C465" : c === "Rose" ? "#E7B4A0" : "#D9D9D9" }} />
            ))}
          </div>
        )}
        <div className="flex items-center justify-between mt-2">
          <p className="font-semibold gold-num text-[15px]" data-testid={`price-${product.slug}`}>
            {inr(product.price_min)}
            {product.price_max > product.price_min && <span className="text-ink-2 font-normal text-xs"> +</span>}
          </p>
          {hasRating && (
            <span className="inline-flex items-center gap-1 text-[11px] text-ink-2" data-testid={`rating-${product.slug}`}>
              <Star size={11} className="text-gold" fill="currentColor" />
              {product.rating_avg} ({product.rating_count})
            </span>
          )}
        </div>
        {deal && (
          <p className="text-[11px] text-[#2E7D5B] mt-1" data-testid={`coupon-line-${product.slug}`}>
            Get it for {inr(deal.final)} with {deal.code}
          </p>
        )}
      </div>
    </div>
  );
}
